import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { verifyOTP } from '@assetron/common/api/auth';
import HeaderFooterLayout from '@assetron/web-common/layout/HeaderFooterLayout';
import { ResponseStatusCode } from '@assetron/common/types/responseStatus';
import FailureDialog from './FailureDialog';
import Loader from '@assetron/web-common/components/common/Loader';
import { useNavigation } from 'auth/src/navigation/useNavigation';
import useInviteValidation from '../../hooks/useInviteValidation';
import {
  INVITE_DETAILS,
  INVITED_ORG_ID,
  ORG_NAME,
  USER_INVITE_PURPOSE,
} from '@assetron/common/constants/constant';
import setSameSiteCookieWithoutExpiry from '@assetron/common/utils/setSameSiteCookieWithoutExpiry';
import { USE_CUSTOM_TOKEN } from '@assetron/web-common/constants/LocalStorageConstants';
import { GA_EVENTS } from '@assetron/common/constants/googleAnalyticsEvents';
import { trackGaEvent } from '@assetron/common/utils/googleAnalytics';

function AcceptInvite() {
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const [isValid, setIsValid] = useState(false);
  const { navigateToSignUpLanding } = useNavigation();
  const { setIsInviteValidating } = useInviteValidation();
  const hasValidatedRef = useRef(false);

  const queryParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );

  const name = queryParams.get('name');
  const token = queryParams.get('token');
  const email = queryParams.get('email');
  const orgId = queryParams.get('org_id');
  const orgName = queryParams.get('org_name');
  const shouldCallCustomToken = queryParams.get('use_custom_token');

  // Set cookie if use_custom_token parameter is 'true'
  useEffect(() => {
    if (shouldCallCustomToken === 'true') {
      setSameSiteCookieWithoutExpiry(USE_CUSTOM_TOKEN, 'true');
    }
  }, [shouldCallCustomToken]);

  const validateInviteMutation = useMutation({
    mutationFn: verifyOTP,
    onSuccess: (data) => {
      setIsInviteValidating(false);
      const { status: statusCode } = data;

      if (
        statusCode === ResponseStatusCode.OK ||
        statusCode === ResponseStatusCode.NoContent
      ) {
        trackGaEvent(GA_EVENTS.AUTH_INVITE_VALIDATION_SUCCESS, {
          status_code: statusCode,
        });
        sessionStorage.setItem(
          INVITE_DETAILS,
          JSON.stringify({ name, token, email, orgId, orgName }),
        );
        if (orgId) {
          setSameSiteCookieWithoutExpiry(INVITED_ORG_ID, orgId);
        }
        navigateToSignUpLanding();
      } else {
        trackGaEvent(GA_EVENTS.AUTH_INVITE_VALIDATION_FAILURE, {
          status_code: statusCode,
        });
        setIsValid(false);
      }
      setIsLoading(false);
    },
    onError: () => {
      trackGaEvent(GA_EVENTS.AUTH_INVITE_VALIDATION_FAILURE, {
        failure_reason: 'request_error',
      });
      setIsInviteValidating(false);
      if (orgName) {
        globalThis.sessionStorage.setItem(ORG_NAME, orgName);
      }
      setIsValid(false);
      setIsLoading(false);
    },
  });

  useEffect(() => {
    if (email && token && orgId && !hasValidatedRef.current) {
      hasValidatedRef.current = true;
      trackGaEvent(GA_EVENTS.AUTH_INVITE_VALIDATION_START, {
        has_use_custom_token: shouldCallCustomToken === 'true',
      });
      setIsInviteValidating(true);
      validateInviteMutation.mutate({
        Email: email,
        OTPCode: token,
        Purpose: USER_INVITE_PURPOSE,
        companyId: orgId,
      });
    } else if (!email || !token || !orgId) {
      setIsValid(false);
      setIsLoading(false);
      setIsInviteValidating(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email, token, orgId]);

  if (isLoading) {
    return (
      <HeaderFooterLayout>
        <Loader />
      </HeaderFooterLayout>
    );
  }

  if (!isValid) {
    return (
      <HeaderFooterLayout>
        <FailureDialog />
      </HeaderFooterLayout>
    );
  }

  return null;
}

export default AcceptInvite;
